/**
 * Field types: how one property of an entity is quantised, compared and put
 * on the wire. A field turns a value into its *stored* form (`canon`): the
 * quantised integer, the enum index, and so on. The server keeps stored
 * forms to see what changed, so a position that moved by less than its
 * quantum is not sent; and it is the stored form that is written, so a
 * value is never rounded twice.
 */
import type { Reader, Writer } from "./bytes";

export interface Field<V, S = V> {
  /** The stored (quantised) form of a value. */
  canon(v: V): S;
  equals(a: S, b: S): boolean;
  write(w: Writer, s: S): void;
  read(r: Reader): V;
}

/** A field whose stored form is nobody else's business. */
export type AnyField<V> = Field<V, any>;

const TAU = Math.PI * 2;
const same = <S>(a: S, b: S) => a === b;
const identity = <V>(v: V) => v;

function scalar<V>(write: (w: Writer, v: V) => void, read: (r: Reader) => V, canon: (v: V) => V = identity): Field<V> {
  return { canon, equals: same, write, read };
}

/** A number quantised to 1/`scale`: stored and sent as the integer. */
function quantised(scale: number, signed: boolean): Field<number, number> {
  return {
    canon: signed ? (v) => Math.round(v * scale) : (v) => Math.max(0, Math.round(v * scale)),
    equals: same,
    write: signed ? (w, s) => w.svar(s) : (w, s) => w.uvar(s),
    read: signed ? (r) => r.svar() / scale : (r) => r.uvar() / scale,
  };
}

export interface Vec2Like {
  x: number;
  y: number;
}

interface StoredVec2 {
  x: number;
  y: number;
}

/** Presence tags for nullable/optional/maybe. */
const ABSENT = 0;
const NULL = 1;
const PRESENT = 2;

function withPresence<V, S>(inner: Field<V, S>, absent: null | undefined): Field<V | null | undefined, S | null | undefined> {
  return {
    canon: (v) => (v === null ? null : v === undefined ? undefined : inner.canon(v)),
    equals: (a, b) => (a === null || a === undefined || b === null || b === undefined ? a === b : inner.equals(a, b)),
    write: (w, s) => {
      if (s === undefined) w.u8(ABSENT);
      else if (s === null) w.u8(NULL);
      else {
        w.u8(PRESENT);
        inner.write(w, s);
      }
    },
    read: (r) => {
      const tag = r.u8();
      if (tag === PRESENT) return inner.read(r);
      // A field that can't be undefined decodes an absent value as null, and the other way round.
      return tag === NULL ? (absent === undefined ? null : absent) : absent;
    },
  };
}

export const f = {
  bool: (): Field<boolean> =>
    scalar(
      (w, v) => w.u8(v ? 1 : 0),
      (r) => r.u8() !== 0,
      (v) => !!v,
    ),
  u8: (): Field<number> =>
    scalar(
      (w, v) => w.u8(v),
      (r) => r.u8(),
    ),
  i8: (): Field<number> =>
    scalar(
      (w, v) => w.i8(v),
      (r) => r.i8(),
    ),
  u16: (): Field<number> =>
    scalar(
      (w, v) => w.u16(v),
      (r) => r.u16(),
    ),
  i16: (): Field<number> =>
    scalar(
      (w, v) => w.i16(v),
      (r) => r.i16(),
    ),
  u32: (): Field<number> =>
    scalar(
      (w, v) => w.u32(v),
      (r) => r.u32(),
    ),
  i32: (): Field<number> =>
    scalar(
      (w, v) => w.i32(v),
      (r) => r.i32(),
    ),
  /** A float at single precision: 4 bytes, and compared at that precision. */
  f32: (): Field<number> =>
    scalar(
      (w, v) => w.f32(v),
      (r) => r.f32(),
      (v) => Math.fround(v),
    ),
  f64: (): Field<number> =>
    scalar(
      (w, v) => w.f64(v),
      (r) => r.f64(),
    ),
  /** A non-negative integer as a varint: one byte under 128, two under 16384. */
  uint: (): Field<number> =>
    scalar(
      (w, v) => w.uvar(v),
      (r) => r.uvar(),
      (v) => Math.max(0, Math.round(v)),
    ),
  /** An integer of either sign as a zigzag varint. */
  int: (): Field<number> =>
    scalar(
      (w, v) => w.svar(v),
      (r) => r.svar(),
      (v) => Math.round(v),
    ),
  /** A number to the nearest 1/`scale`, either sign (e.g. 128 for positions in blocks: three bytes within +-8192). */
  quant: (scale: number): Field<number, number> => quantised(scale, true),
  /** A number to the nearest 1/`scale`, never negative (one byte less than `quant` for the same range). */
  uquant: (scale: number): Field<number, number> => quantised(scale, false),
  /**
   * An angle in radians, sent as 16 bits round the circle (to about 0.0001
   * rad) or 8 (about 1.4 degrees), and read back in (-pi, pi] (where atan2
   * puts angles, so one that was there comes back where it was).
   */
  angle: (bits: 8 | 16 = 16): Field<number, number> => {
    const steps = bits === 16 ? 65536 : 256;
    const read = (s: number) => {
      const a = (s / steps) * TAU;
      return a > Math.PI ? a - TAU : a;
    };
    return {
      canon: (v) => {
        const s = Math.round((v / TAU) * steps) % steps;
        return s < 0 ? s + steps : s;
      },
      equals: same,
      write: bits === 16 ? (w, s) => w.u16(s) : (w, s) => w.u8(s),
      read: bits === 16 ? (r) => read(r.u16()) : (r) => read(r.u8()),
    };
  },
  /** A string (`f.str<ItemId>()` for a property typed as a union of strings). */
  str: <V extends string = string>(): Field<V> =>
    scalar<V>(
      (w, v) => w.str(v),
      (r) => r.str() as V,
    ),
  /**
   * Not sent at all: the receiver gets `value`. For state only the server
   * needs (input bookkeeping, say) that still has to be on the type.
   */
  omit: <V>(value: V): Field<V, 0> => ({
    canon: () => 0,
    equals: () => true,
    write: () => {},
    read: () => value,
  }),
  /** One of a fixed list of values (strings, usually), sent as its index. */
  enumOf: <V>(values: readonly V[]): Field<V, number> => {
    if (values.length > 256) throw new RangeError("netsync: enumOf takes at most 256 values");
    return {
      canon: (v) => {
        const i = values.indexOf(v);
        if (i < 0) throw new RangeError(`netsync: ${String(v)} is not one of the enum's values`);
        return i;
      },
      equals: same,
      write: (w, s) => w.u8(s),
      read: (r) => values[r.u8()]!,
    };
  },
  /** An {x, y} pair, each to the nearest 1/`scale`. */
  vec2: <V extends Vec2Like = Vec2Like>(scale: number): Field<V, StoredVec2> => ({
    canon: (v) => ({ x: Math.round(v.x * scale), y: Math.round(v.y * scale) }),
    equals: (a, b) => a.x === b.x && a.y === b.y,
    write: (w, s) => {
      w.svar(s.x);
      w.svar(s.y);
    },
    read: (r) => ({ x: r.svar() / scale, y: r.svar() / scale }) as V,
  }),
  /** `inner`, or null. */
  nullable: <V, S>(inner: Field<V, S>): Field<V | null, S | null | undefined> => withPresence(inner, null) as Field<V | null, S | null | undefined>,
  /** `inner`, or undefined (an optional property: left off the decoded object). */
  optional: <V, S>(inner: Field<V, S>): Field<V | undefined, S | null | undefined> => withPresence(inner, undefined) as Field<V | undefined, S | null | undefined>,
  /** `inner`, null or undefined (an optional property that can also be null). */
  maybe: <V, S>(inner: Field<V, S>): Field<V | null | undefined, S | null | undefined> => withPresence(inner, undefined),
  /**
   * A nested object with fields of its own. Sent whole whenever any part of
   * it changes, so keep these small; put anything big and busy in an entity
   * type of its own.
   */
  obj: <T extends object>(fields: { [K in keyof T]-?: AnyField<T[K]> }): Field<T, unknown[]> => {
    const names = Object.keys(fields) as (keyof T & string)[];
    const list = names.map((n) => fields[n] as Field<unknown, unknown>);
    return {
      canon: (v) => list.map((fld, i) => fld.canon(v[names[i]!])),
      equals: (a, b) => {
        for (let i = 0; i < list.length; i++) if (!list[i]!.equals(a[i], b[i])) return false;
        return true;
      },
      write: (w, s) => {
        for (let i = 0; i < list.length; i++) list[i]!.write(w, s[i]);
      },
      read: (r) => {
        const out = {} as T;
        for (let i = 0; i < list.length; i++) {
          const v = list[i]!.read(r);
          if (v !== undefined) out[names[i]!] = v as T[keyof T & string];
        }
        return out;
      },
    };
  },
  /**
   * A Record<string, V>, sent whole when any entry changes. `keys: "num"`
   * is for records keyed by integers written as strings (`Record<string, V>`
   * indexed by a cell or anchor number): the key goes as a varint.
   */
  record: <V, S>(keys: "str" | "num", inner: Field<V, S>): Field<Record<string, V>, [string, S][]> => ({
    canon: (v) => Object.entries(v).map(([k, x]) => [k, inner.canon(x)]),
    equals: (a, b) => {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i]![0] !== b[i]![0] || !inner.equals(a[i]![1], b[i]![1])) return false;
      return true;
    },
    write: (w, s) => {
      w.uvar(s.length);
      for (const [k, x] of s) {
        if (keys === "num") w.uvar(+k);
        else w.str(k);
        inner.write(w, x);
      }
    },
    read: (r) => {
      const out: Record<string, V> = {};
      const n = r.uvar();
      for (let i = 0; i < n; i++) {
        const k = keys === "num" ? String(r.uvar()) : r.str();
        out[k] = inner.read(r);
      }
      return out;
    },
  }),
  /** An array, sent whole when any element changes. */
  array: <V, S>(inner: Field<V, S>): Field<V[], S[]> => ({
    canon: (v) => v.map((x) => inner.canon(x)),
    equals: (a, b) => {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (!inner.equals(a[i]!, b[i]!)) return false;
      return true;
    },
    write: (w, s) => {
      w.uvar(s.length);
      for (const x of s) inner.write(w, x);
    },
    read: (r) => {
      const n = r.uvar();
      const out: V[] = [];
      for (let i = 0; i < n; i++) out.push(inner.read(r));
      return out;
    },
  }),
  /** Anything JSON can carry, as a JSON string. The fallback when nothing above fits; never the first choice. */
  json: <V>(): Field<V, string> => ({
    canon: (v) => JSON.stringify(v),
    equals: same,
    write: (w, s) => w.str(s),
    read: (r) => JSON.parse(r.str()) as V,
  }),
};
